import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Eye, Trash2, Download, X, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import AdminThemeToggle from "@/components/AdminThemeToggle";
import { devWarn } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminContestEntries() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [contestFilter, setContestFilter] = useState("all");

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

  useEffect(() => {
    if (!authLoading && !user) navigate("/admin/login");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/admin/contest-entries`, { headers: getHeaders() })
      .then(r => setEntries(r.data))
      .catch(err => devWarn("Failed to load contest entries:", err.message))
      .finally(() => setLoading(false));
  }, [user]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/contest-entries/${id}`, { status }, { headers: getHeaders() });
      setEntries(entries.map(e => e.id === id ? { ...e, status } : e));
      if (selected && selected.id === id) setSelected({ ...selected, status });
      toast.success(status === "winner" ? "Markerad som vinnare" : status === "disqualified" ? "Diskvalificerad" : "Status uppdaterad");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte uppdatera");
    }
  };

  const saveWinner = async (id, week, prize) => {
    const w = parseInt(week, 10);
    if (!Number.isFinite(w) || w < 1) { toast.error("Ange ett giltigt veckonummer"); return; }
    if (!prize || !prize.trim()) { toast.error("Ange prisbeskrivning"); return; }
    try {
      await axios.put(
        `${API}/admin/contest-entries/${id}`,
        { status: "winner", winner_week: w, winner_prize: prize.trim() },
        { headers: getHeaders() }
      );
      const patch = { status: "winner", winner_week: w, winner_prize: prize.trim() };
      setEntries(entries.map(e => e.id === id ? { ...e, ...patch } : e));
      if (selected && selected.id === id) setSelected({ ...selected, ...patch });
      toast.success(`Sparad som vinnare vecka ${w}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara vinnaren");
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Radera detta bidrag? Detta går inte att ångra.")) return;
    try {
      await axios.delete(`${API}/admin/contest-entries/${id}`, { headers: getHeaders() });
      setEntries(entries.filter(e => e.id !== id));
      if (selected && selected.id === id) setSelected(null);
      toast.success("Raderat");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte radera");
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Tävling", "Datum", "Namn", "E-post", "Telefon", "Slogan-ord", "Status", "Slogan"].join(";"),
      ...filtered.map(e => [
        e.contest_slug || "",
        new Date(e.created_at).toLocaleString("sv-SE"),
        `"${(e.name || "").replace(/"/g, "''")}"`,
        e.email,
        e.phone || "",
        e.slogan_word_count,
        e.status,
        `"${(e.slogan || "").replace(/"/g, "''").replace(/\n/g, " ")}"`,
      ].join(";")),
    ].join("\n");
    const blob = new Blob(["\ufeff" + rows], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const suffix = contestFilter === "all" ? "alla" : contestFilter;
    a.href = url; a.download = `tavlingsbidrag-${suffix}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (authLoading || !user) return <div className="min-h-screen bg-[#0A0A0A]" />;

  const byContest = contestFilter === "all" ? entries : entries.filter(e => (e.contest_slug || "fotbollsmorgon") === contestFilter);
  const filtered = statusFilter === "all" ? byContest : byContest.filter(e => e.status === statusFilter);
  const counts = { all: byContest.length, pending: byContest.filter(e => e.status === "pending").length, winner: byContest.filter(e => e.status === "winner").length, disqualified: byContest.filter(e => e.status === "disqualified").length };
  // Bucket sizes across ALL entries — shown in the contest dropdown so admin sees at-a-glance how many entries per contest
  const contestCounts = entries.reduce((acc, e) => {
    const k = e.contest_slug || "fotbollsmorgon"; acc[k] = (acc[k] || 0) + 1; return acc;
  }, {});

  const statusPill = (s) => {
    if (s === "winner") return "border-emerald-500/30 text-emerald-400 bg-emerald-500/5";
    if (s === "disqualified") return "border-red-500/30 text-red-400 bg-red-500/5";
    return "border-white/20 text-white/50";
  };
  const statusLabel = (s) => ({ winner: "Vinnare", disqualified: "Diskvalificerad", pending: "Väntar" }[s] || s);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-8">
            <Link to="/admin" className="font-['Playfair_Display'] text-xl text-white">SERAVA<span className="text-[#C5A059]"> ADMIN</span></Link>
            <nav className="flex items-center gap-6">
              <Link to="/admin/events" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Events</Link>
              <Link to="/admin/pages" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Sidor</Link>
              <Link to="/admin/orders" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Ordrar</Link>
              <Link to="/admin/contest-entries" className="text-white text-xs tracking-wider uppercase font-['Outfit']">Tävling</Link>
              <Link to="/admin/settings" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Inställningar</Link>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <AdminThemeToggle />
            <button onClick={logout} className="text-white/50 hover:text-white text-xs tracking-wider uppercase font-['Outfit'] flex items-center gap-1.5"><LogOut size={14} /> Logga ut</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-['Playfair_Display'] text-3xl text-white">Tävlingsbidrag</h1>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-xs tracking-wider uppercase text-white/50 font-['Outfit']" data-testid="contest-filter-wrapper">
              <span>Tävling</span>
              <select value={contestFilter} onChange={(e) => setContestFilter(e.target.value)} className="bg-[#141414] border border-[#262626] text-white px-3 py-2 text-xs tracking-[0.15em] uppercase font-['Outfit'] focus:outline-none focus:border-[#C5A059]" data-testid="contest-filter">
                <option value="all">Alla ({entries.length})</option>
                <option value="fotbollsmorgon">Fotbollsmorgon ({contestCounts.fotbollsmorgon || 0})</option>
                <option value="scandsports">Scand Sports ({contestCounts.scandsports || 0})</option>
              </select>
            </label>
            <button onClick={exportCsv} disabled={filtered.length === 0} className="btn-secondary px-5 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2 disabled:opacity-40" data-testid="contest-export-csv">
              <Download size={14} /> Exportera CSV ({filtered.length})
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { key: "all", label: "Alla" },
            { key: "pending", label: "Väntar" },
            { key: "winner", label: "Vinnare" },
            { key: "disqualified", label: "Diskvalificerade" },
          ].map(f => (
            <button key={f.key} onClick={() => setStatusFilter(f.key)}
              className={`px-4 py-2 text-xs tracking-[0.15em] uppercase font-['Outfit'] border transition-colors ${statusFilter === f.key ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-[#262626] text-white/50 hover:text-white"}`}
              data-testid={`filter-${f.key}`}>
              {f.label} <span className="text-white/30 ml-1">({counts[f.key]})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-white/40 font-['Outfit'] text-sm py-12 text-center">Laddar...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#262626]">
            <p className="text-white/40 font-['Outfit'] text-sm">Inga bidrag {statusFilter !== "all" ? "i denna kategori" : "än"}</p>
          </div>
        ) : (
          <div className="border border-[#262626]">
            <table className="w-full">
              <thead className="bg-[#141414] border-b border-[#262626]">
                <tr className="text-left">
                  <th className="p-4 text-xs tracking-wider uppercase text-white/40 font-['Outfit'] font-normal">Datum</th>
                  <th className="p-4 text-xs tracking-wider uppercase text-white/40 font-['Outfit'] font-normal">Tävling</th>
                  <th className="p-4 text-xs tracking-wider uppercase text-white/40 font-['Outfit'] font-normal">Namn</th>
                  <th className="p-4 text-xs tracking-wider uppercase text-white/40 font-['Outfit'] font-normal">E-post</th>
                  <th className="p-4 text-xs tracking-wider uppercase text-white/40 font-['Outfit'] font-normal">Status</th>
                  <th className="p-4 text-right text-xs tracking-wider uppercase text-white/40 font-['Outfit'] font-normal">Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => (
                  <tr key={e.id} className="border-b border-[#262626] last:border-0 hover:bg-[#141414]/50 transition-colors" data-testid={`entry-row-${e.id}`}>
                    <td className="p-4 text-white/60 font-['Outfit'] text-sm">{new Date(e.created_at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}</td>
                    <td className="p-4 text-white/70 font-['Outfit'] text-xs uppercase tracking-wider">{(e.contest_slug || "fotbollsmorgon") === "scandsports" ? "Scand Sports" : "Fotbollsmorgon"}</td>
                    <td className="p-4 text-white font-['Outfit'] text-sm">{e.name}</td>
                    <td className="p-4 text-white/60 font-['Outfit'] text-sm">{e.email}</td>
                    <td className="p-4"><span className={`text-xs px-2 py-1 border ${statusPill(e.status)}`}>{statusLabel(e.status)}{e.status === "winner" && e.winner_week ? ` · v${e.winner_week}` : ""}</span></td>
                    <td className="p-4 text-right">
                      <button onClick={() => setSelected(e)} className="text-white/40 hover:text-[#C5A059] p-1.5" title="Visa" data-testid={`view-${e.id}`}><Eye size={16} /></button>
                      <button onClick={() => deleteEntry(e.id)} className="text-white/40 hover:text-red-400 p-1.5 ml-1" title="Radera" data-testid={`delete-${e.id}`}><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail modal */}
        {selected && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="entry-detail-modal">
            <div className="bg-[#141414] border border-[#262626] max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#262626] sticky top-0 bg-[#141414]">
                <h2 className="font-['Playfair_Display'] text-xl text-white">{selected.name}</h2>
                <button onClick={() => setSelected(null)} className="text-white/40 hover:text-white" data-testid="close-detail"><X size={18} /></button>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4 text-sm font-['Outfit']">
                  <div><p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Tävling</p><p className="text-[#C5A059] uppercase tracking-wider text-xs">{(selected.contest_slug || "fotbollsmorgon") === "scandsports" ? "Scand Sports" : "Fotbollsmorgon"}</p></div>
                  <div><p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Skickad</p><p className="text-white">{new Date(selected.created_at).toLocaleString("sv-SE")}</p></div>
                  <div><p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">E-post</p><p className="text-white">{selected.email}</p></div>
                  <div><p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Telefon</p><p className="text-white">{selected.phone || "—"}</p></div>
                  {selected.quiz_total != null && selected.quiz_total > 0 && (
                    <div className="col-span-2"><p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Quiz</p><p className="text-[#C5A059]">{selected.quiz_score} av {selected.quiz_total} rätt · svar: {(selected.quiz_answers || []).join(", ").toUpperCase()}</p></div>
                  )}
                </div>

                <div>
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mb-2 font-['Outfit']">Slogan ({selected.slogan_word_count} ord)</p>
                  <div className="border border-[#262626] bg-[#0A0A0A] p-4 text-white font-['Cormorant_Garamond'] text-lg leading-relaxed whitespace-pre-wrap">{selected.slogan}</div>
                </div>

                <div className="border-t border-[#262626] pt-5">
                  {/* Winner details (week + prize). Shown always so admin can edit even after marking. */}
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mb-3 font-['Outfit']">Vinnardetaljer (visas publikt på kampanjsidan)</p>
                  <div className="grid grid-cols-1 md:grid-cols-[120px_1fr_auto] gap-3 mb-4">
                    <input
                      type="number"
                      min="1"
                      placeholder="Vecka"
                      defaultValue={selected.winner_week ?? ""}
                      onChange={(e) => setSelected({ ...selected, _winner_week_draft: e.target.value })}
                      className="bg-[#0A0A0A] border border-[#262626] text-white px-3 py-3 text-sm font-['Outfit'] focus:outline-none focus:border-[#C5A059]"
                      data-testid="winner-week-input"
                    />
                    <input
                      type="text"
                      placeholder="Pris (t.ex. VIP-biljetter Man Utd vs Atlético Madrid)"
                      defaultValue={selected.winner_prize ?? ""}
                      onChange={(e) => setSelected({ ...selected, _winner_prize_draft: e.target.value })}
                      className="bg-[#0A0A0A] border border-[#262626] text-white px-3 py-3 text-sm font-['Outfit'] focus:outline-none focus:border-[#C5A059]"
                      data-testid="winner-prize-input"
                    />
                    <button
                      onClick={() => saveWinner(
                        selected.id,
                        selected._winner_week_draft ?? selected.winner_week,
                        selected._winner_prize_draft ?? selected.winner_prize,
                      )}
                      className="btn-primary px-5 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] whitespace-nowrap"
                      data-testid="save-winner"
                    >
                      Spara vinnare
                    </button>
                  </div>
                  {selected.winner_week && (
                    <p className="text-emerald-400/80 text-xs font-['Outfit'] mb-4">
                      Sparad: Vecka {selected.winner_week} · {selected.winner_prize || "(inget pris angett)"}
                    </p>
                  )}
                </div>

                <div className="border-t border-[#262626] pt-5 flex gap-3 flex-wrap">
                  <button onClick={() => updateStatus(selected.id, "disqualified")} className={`px-5 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2 transition-colors ${selected.status === "disqualified" ? "bg-red-500/20 border border-red-500/40 text-red-300" : "border border-[#262626] text-white/70 hover:border-red-500/40 hover:text-red-400"}`} data-testid="mark-disqualified">
                    <XCircle size={14} /> Diskvalificera
                  </button>
                  {selected.status !== "pending" && (
                    <button onClick={() => updateStatus(selected.id, "pending")} className="px-5 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] border border-[#262626] text-white/50 hover:text-white" data-testid="mark-pending">
                      Återställ
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
